import { type FC } from 'react'
import { Modal } from '../../../components/Modal/Modal'
import { type SchedulingEventType } from '../../../models/Scheduling'
import { useAuthenticatedUser } from '../../../common/useAuthenticatedUser'
import { useSearchSchedulingLinksQuery } from '../../../store/scheduling/scheduling'

export type EventTypeImpactVariant = 'delete' | 'deactivate'

interface EventTypeImpactDialogProps {
  variant: EventTypeImpactVariant
  eventType: SchedulingEventType
  staysOnAccountPage?: boolean
  isLoading?: boolean
  onConfirm: () => void
  onCancel: () => void
}

const VARIANT_CONFIG: Record<EventTypeImpactVariant, { title: string; confirmLabel: string; danger: boolean; bodyCopy: string }> = {
  delete: {
    title: 'Deleting this event type?',
    confirmLabel: 'Delete',
    danger: true,
    bodyCopy: "will be removed from users' booking pages. Clients can no longer book it. Existing bookings keep their scheduled times.",
  },
  deactivate: {
    title: 'Set this event type to inactive?',
    confirmLabel: 'Confirm',
    danger: false,
    bodyCopy:
      "is removed from users' booking pages while inactive. Clients can't book it until you set it back to active. Existing bookings keep their scheduled times.",
  },
}

const DEACTIVATE_STAYS_ON_ACCOUNT_PAGE_COPY =
  "is removed from users' booking pages while inactive. Clients can still book it on your account booking page. Existing bookings keep their scheduled times."

export const EventTypeImpactDialog: FC<EventTypeImpactDialogProps> = ({ variant, eventType, staysOnAccountPage = false, isLoading, onConfirm, onCancel }) => {
  const accountId = useAuthenticatedUser().accountId
  const { data: activeLinks, isFetching } = useSearchSchedulingLinksQuery(
    { accountId, eventTypeId: eventType.id, active: true, pageRequest: { page: 0, size: 1 } },
    { refetchOnMountOrArgChange: true },
  )
  const config = VARIANT_CONFIG[variant]
  const bodyCopy = variant === 'deactivate' && staysOnAccountPage ? DEACTIVATE_STAYS_ON_ACCOUNT_PAGE_COPY : config.bodyCopy
  const activeLinkCount = activeLinks?.page.totalElements ?? 0

  return (
    <Modal
      show
      onHide={onCancel}
      header={<h5 className='mb-0'>{config.title}</h5>}
      headerBodySeparator
      size='lg'
      body={
        <div className='d-flex flex-column gap-4'>
          <span className='paragraph-medium'>
            &quot;<span className='bold'>{eventType.name}</span>&quot; {bodyCopy}
          </span>
          {activeLinkCount > 0 ? (
            <div className='d-flex align-items-center gap-3 py-2 px-3 rounded-3 zocks-border-color-warning-dark paragraph-medium'>
              <i className='bi bi-exclamation-triangle-fill zocks-text-warning-dark flex-shrink-0' />
              <span>
                {activeLinkCount} booking link{activeLinkCount === 1 ? '' : 's'} awaiting client response will stop working after you confirm this change.
              </span>
            </div>
          ) : null}
        </div>
      }
      showFooterActions
      footerPrimaryActionTitle={config.confirmLabel}
      footerPrimaryActionButtonVariant={config.danger ? 'danger' : 'primary'}
      footerPrimaryActionLoading={isLoading}
      footerPrimaryActionDisabled={isFetching}
      footerSecondaryActionTitle='Cancel'
      onFooterPrimaryAction={onConfirm}
      onFooterSecondaryAction={onCancel}
    />
  )
}
